import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    AlertIcon,
    Box,
    Button,
    Center,
    Checkbox,
    HStack,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Spinner,
    VStack,
    useToast,
} from '@chakra-ui/react';
import { useMutation, useQuery } from '@apollo/client';
import { useModal, useViewer } from '~/hooks';
import { join } from '~/utils';
import { pxReportReducer } from '~/reducers/pxReportReducer';
import {
    GetProjectDocument,
    UpdateProjectMetadataDocument,
    type Projekt,
} from '~/gql/ucpw/graphql';
import { ServiceRequestOriginView } from '~/components/Renderers';
import {
    collectVisibleQuestions,
    connectionFromMetadata,
    extractDraftAnswers,
    extractSchadenursache,
    insurerFromMetadata,
    mergePxConnectSchadenberichtMetadata,
    parseMetadata,
    pxConnectDraftFromMetadata,
    snapshotDateLabel,
    sourceSystemFromMetadata,
    useServiceRequestQuestionCatalog,
    validatePxReport,
    assertProjectMetadataSaved,
    type PxBearbeitungsstand,
    type PxDraftAnswer,
    type PxQuestion,
} from '~/meta/data/px-connect';
import { PxQuestionList } from './PxQuestionList';
import { usePxConnectKorrespondenz } from './usePxConnectKorrespondenz';
import log from '~/log';

interface PxConnectModalProps {
    /** MultiModal slot id; matched against `state.modals.activeModal`. */
    id?: string;
    projectId?: number;
    subprojectId?: number;
    /** Service request id (from the subproject metadata) that keys the catalog. */
    serviceRequestId?: string;
    /** Damage object address (Schadensobjekt) shown in the PDF header. */
    schadensobjekt?: string;
    /** Claim number (Schaden-Nr.) shown in the PDF header. */
    schadenNr?: string;
    /**
     * Metadata of the same subproject the `serviceRequestId` comes from — the origin
     * line in the header („LVM (PXC) [emulate]") is read from it, so it names the
     * order whose catalog is shown, not some other subproject's.
     */
    subprojectMetadata?: unknown;
}

/**
 * PX Connect "Schadenbericht erstellen" modal (UCPWO-378).
 *
 * Fetches the insurer's Fragenkatalog by the subproject's `serviceRequestId`,
 * renders it via the data-driven {@link PxQuestionList}, and manages answer state
 * with a dedicated reducer.
 *
 * "Korrespondenz vorbereiten" routes to project details and opens the standard
 * Korrespondenz modal prefilled as Typ „PX Connect Nachricht" / Subtyp
 * „Schadenbericht"; the two footer checkboxes pick which PDFs get attached —
 * the Fragenkatalog (on by default) and/or the Leckortungsbericht (off by default).
 */
export const PxConnectModal: React.FC<PxConnectModalProps> = ({
    projectId,
    subprojectId,
    serviceRequestId,
    schadensobjekt,
    schadenNr,
    subprojectMetadata,
}) => {
    const { isOpen, onClose, dispatch, state } = useModal();
    const navigate = useNavigate();
    const toast = useToast();
    const context = { clientName: 'ucpw' };
    // „angelegt durch"-Snapshot: Mitarbeiter-Id + Anzeigename der Erstanlage.
    const viewer = useViewer();
    const mitarbeiterId = viewer?.app?.mitarbeiterId;
    const createdByName = join([viewer?.employee?.name, viewer?.employee?.vorname], ', ');
    const { prepareKorrespondenzDraft, ready } = usePxConnectKorrespondenz({
        projectId,
        subprojectId,
    });
    const report = state?.modals?.props?.report;
    // Herkunft wie im Seitenkopf und im Auftragseingang (UCPWO-398), aber bewusst ohne
    // Rückfall aufs Projekt: der Dialog gehört zu genau einem Auftrag.
    const originSource = sourceSystemFromMetadata(subprojectMetadata);
    const originInsurer = insurerFromMetadata(subprojectMetadata);
    const originConnection = connectionFromMetadata(subprojectMetadata);

    const { data: projectData, loading: projectLoading } = useQuery(GetProjectDocument, {
        variables: { id: projectId as number },
        context,
        skip: !isOpen || !projectId,
        fetchPolicy: 'network-only',
    });
    const project = projectData?.item?.item as Projekt | undefined;
    const [updateProjectMetadata] = useMutation(UpdateProjectMetadataDocument, { context });
    const [savingDraft, setSavingDraft] = React.useState(false);

    // A draft only applies to the same order it was saved for — a stale draft
    // from a previous service request must not resurrect old answers.
    // `metadata` reaches the client either as a JSON string or an already-parsed
    // object depending on the resolver — parseMetadata handles both.
    const draft =
        (parseMetadata(project?.metadata)?.pxConnectDraft as
            | { serviceRequestId?: string; answers?: PxDraftAnswer[] }
            | undefined) || {};
    const resumableAnswers: PxDraftAnswer[] | undefined =
        draft.serviceRequestId === serviceRequestId &&
        Array.isArray(draft.answers) &&
        draft.answers.length
            ? draft.answers
            : undefined;

    const { catalog, loading, error, fromSnapshot, snapshotSavedAt } =
        useServiceRequestQuestionCatalog(serviceRequestId, {
            skip: !isOpen,
        });
    const snapshotSavedAtLabel = snapshotDateLabel(snapshotSavedAt);
    // Der Resolver liefert `null` (statt eines Fehlers), wenn er zum Auftrag keinen
    // Katalog auflösen kann — kein Auftragsdatensatz, keine Versicherer-Kennung, oder
    // nichts im SVC-Cache. Ohne eigenen Zustand bliebe `initialized` false und das
    // Modal drehte ewig weiter (Staging 2026-07-30). Fehlende `serviceRequestId`
    // (Query geskippt) landet bewusst im selben Zustand.
    const noCatalog = !loading && !error && !catalog;
    const [questions, formDispatch] = React.useReducer(pxReportReducer, [] as PxQuestion[]);

    // INIT must fire exactly once per "open" — later re-renders happen because our
    // own "Speichern" writes back into the same GetProject cache entry, and
    // re-initializing from that echo would clobber edits made after the save.
    // Tracked as state (not a ref) so the modal body can keep showing the loading
    // spinner until INIT actually runs — catalog and project metadata resolve
    // independently, and the catalog can finish first while the project query
    // (needed for the resumable draft) is still in flight.
    const [initialized, setInitialized] = React.useState(false);
    React.useEffect(() => {
        if (!isOpen) setInitialized(false);
    }, [isOpen]);

    React.useEffect(() => {
        if (!isOpen || initialized) return;
        // Wait for the project metadata to resolve too, so a resumable draft
        // isn't raced by an initial fresh-catalog init.
        if (!catalog || projectLoading) return;
        formDispatch({ type: 'INIT', questions: catalog });
        if (resumableAnswers) formDispatch({ type: 'APPLY_DRAFT', answers: resumableAnswers });
        setInitialized(true);
    }, [isOpen, catalog, projectLoading, resumableAnswers, initialized]);

    const rootKeys = React.useMemo(
        () => questions.filter((question) => !question.parentKey).map((question) => question.key),
        [questions]
    );

    const visibleQuestions = React.useMemo(() => collectVisibleQuestions(questions), [questions]);
    const invalidKeys = React.useMemo(() => validatePxReport(visibleQuestions), [visibleQuestions]);

    // Missing mandatory answers no longer block the Korrespondenz (UCPWO-410, customer
    // meeting 24.09.: incomplete reports go out as well). A click with gaps still
    // marks them, and the send is recorded as „unvollständig".
    const [submitAttempted, setSubmitAttempted] = React.useState(false);
    const errorKeys = submitAttempted ? invalidKeys : new Set<string>();

    // Which PDFs to attach to the Korrespondenz. The Fragenkatalog is on by
    // default; the Leckortungsbericht is off by default (customer decision,
    // 24.09.) and has to be ticked on purpose. It is tied to a specific
    // Einsatzbericht row; when the modal is opened generically (e.g. the
    // Kalkulation header button, no row context), there is no report to attach,
    // so that option is excluded entirely.
    const canAttachBericht = Boolean(report);
    const [attachFragenkatalog, setAttachFragenkatalog] = React.useState(true);
    const [attachBericht, setAttachBericht] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);
    // Solange gespeichert bzw. vorbereitet wird, ist das Formular gesperrt (UCPWO-420):
    // beide Wege arbeiten mit dem Stand beim Klick und schließen danach den Dialog.
    const busy = savingDraft || submitting;

    React.useEffect(() => {
        if (!canAttachBericht) setAttachBericht(false);
    }, [canAttachBericht]);

    const onCancel = () => {
        dispatch?.({ type: 'resetState' });
        onClose?.();
    };

    // Persists the in-progress answers into the project's metadata, keyed under
    // `pxConnectDraft`, without touching the other metadata keys (sourceSystem,
    // serviceRequestId) already stored there. Shared by the explicit "Speichern"
    // button and the "Korrespondenz vorbereiten" flow.
    const persistDraft = async () => {
        if (!projectId || !project) return;
        const existingDraft = pxConnectDraftFromMetadata(project?.metadata);
        const nowIso = new Date().toISOString();
        const result = await updateProjectMetadata({
            variables: {
                projektId: projectId,
                version: project.version,
                forceOverwrite: true,
                // AWSJSON variables must be sent as a JSON string, not a raw object
                // (see subprojektMetadata usage in packages/ucpw-services). mergePxConnect…
                // erhält andere metadata-Schlüssel (sourceSystem, serviceRequestId,
                // pxConnectTermine) sowie ein bereits gesetztes `sentAt`.
                metadata: mergePxConnectSchadenberichtMetadata(project?.metadata, {
                    serviceRequestId,
                    answers: extractDraftAnswers(questions),
                    schadenursache: extractSchadenursache(questions),
                    savedAt: nowIso,
                    // Erstanlage-Felder nur beim ersten Speichern setzen (nie überschreiben).
                    createdAt: existingDraft?.createdAt ?? nowIso,
                    createdBy: existingDraft?.createdBy ?? mitarbeiterId,
                    createdByName: existingDraft?.createdByName ?? createdByName,
                }),
            },
        });
        assertProjectMetadataSaved(result);
    };

    const onSaveDraft = async () => {
        if (!projectId || !project) return;
        setSavingDraft(true);
        try {
            await persistDraft();
            toast({
                title: 'Entwurf gespeichert',
                status: 'success',
                position: 'top',
                duration: 3000,
                isClosable: true,
            });
            // Nach dem Zwischenspeichern schließen: vorher war „Speichern" oben + „Abbrechen"
            // unten der einzige Weg heraus, was wie Verwerfen aussieht, obwohl gespeichert
            // wurde. Der Entwurf ist persistiert, Wiederöffnen setzt ihn per APPLY_DRAFT fort.
            onCancel();
        } catch (err) {
            log.error('PxConnect.saveDraft', err);
            toast({
                title: 'Entwurf konnte nicht gespeichert werden',
                description: (err as Error)?.message,
                status: 'error',
                position: 'top',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setSavingDraft(false);
        }
    };

    const onPrepareCorrespondence = async () => {
        const stand: PxBearbeitungsstand = invalidKeys.size > 0 ? 'unvollstaendig' : 'vollstaendig';
        if (stand === 'unvollstaendig') setSubmitAttempted(true);
        // Prepare a PX Connect Korrespondenz draft (Typ „PX Connect Nachricht" /
        // Subtyp „Schadenbericht") with the selected PDFs attached. The standard
        // Korrespondenz modal opens next, so users can review and decide themselves
        // whether they want to save/send it.
        setSubmitting(true);
        try {
            // Persist the answers into the project metadata first (same as the
            // top-right "Speichern"), so the draft survives the navigation to the
            // Korrespondenz modal even if the PDF/correspondence build below fails.
            await persistDraft();
            const correspondence = await prepareKorrespondenzDraft({
                questions: visibleQuestions,
                schadensobjekt,
                schadenNr,
                reportId: report?.id,
                report,
                attachFragenkatalog,
                attachBericht,
            });
            toast({
                title:
                    stand === 'unvollstaendig'
                        ? 'Korrespondenz vorbereitet – Bericht unvollständig'
                        : 'Korrespondenz vorbereitet',
                description:
                    stand === 'unvollstaendig'
                        ? `${invalidKeys.size} Pflichtfeld(er) offen. Der Bericht wird als unvollständig versendet.`
                        : 'Bitte prüfen Sie die Angaben vor dem Speichern.',
                status: stand === 'unvollstaendig' ? 'warning' : 'success',
                position: 'top',
                duration: 5000,
                isClosable: true,
            });
            dispatch?.({ type: 'resetState' });
            onClose?.();
            navigate(`/projekte/${projectId}/${subprojectId}/details`, {
                state: {
                    openCorrespondenceModal: {
                        // Marker: beim Speichern dieser Korrespondenz stempelt der
                        // CorrespondenceModal `sentAt` in den Schadenbericht-Draft (ADR 0006).
                        // Das Feld ist nur auf dem Row-Objekt (kein Formularfeld) und landet
                        // daher nicht im Korrespondenz-Mutations-Payload.
                        row: {
                            ...correspondence,
                            __pxSchadenberichtProjektId: projectId,
                            __pxSchadenberichtStand: stand,
                        },
                    },
                },
            });
        } catch (err) {
            log.error('PxConnect.prepareKorrespondenz', err);
            toast({
                title: 'Korrespondenz konnte nicht vorbereitet werden',
                description: (err as Error)?.message,
                status: 'error',
                position: 'top',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <ModalContent rounded="none" maxWidth="container.md">
            <ModalHeader
                p={5}
                borderBottomWidth={1}
                borderColor="gray.200"
                data-test-id="px-connect-modal-header"
            >
                <HStack justify="space-between" align="center">
                    <VStack align="flex-start" spacing={1}>
                        <Box>PX Connect Schadenbericht erstellen</Box>
                        {originSource && (
                            <Box
                                fontSize="sm"
                                fontWeight="normal"
                                data-test-id="px-connect-modal-origin"
                            >
                                <ServiceRequestOriginView
                                    insurer={originInsurer}
                                    sourceSystem={{ source: originSource }}
                                    connection={originConnection}
                                />
                            </Box>
                        )}
                    </VStack>
                    <Button
                        data-test-id="px-connect-save-draft"
                        size="sm"
                        variant="outline"
                        isLoading={savingDraft}
                        // Ohne Katalog gibt es nichts zu speichern — und ein Speichern
                        // würde einen bereits vorhandenen Entwurf mit `answers: []`
                        // überschreiben.
                        isDisabled={!project || noCatalog || Boolean(error) || submitting}
                        onClick={onSaveDraft}
                    >
                        Zwischenspeichern
                    </Button>
                </HStack>
            </ModalHeader>
            <ModalBody py={5}>
                {error ? (
                    <Alert status="error">
                        <AlertIcon />
                        Fragenkatalog konnte nicht geladen werden.
                    </Alert>
                ) : noCatalog ? (
                    <Alert status="info" data-test-id="px-connect-empty">
                        <AlertIcon />
                        Für diesen Auftrag ist kein Fragenkatalog hinterlegt.
                    </Alert>
                ) : !initialized ? (
                    <Center py={10}>
                        <Spinner data-test-id="px-connect-loading" />
                    </Center>
                ) : (
                    <>
                        {/* Der Fallback darf nicht unsichtbar sein: sonst sieht ein
                            kaputter Live-Abruf wie Normalbetrieb aus, und niemand merkt,
                            dass der Katalog veraltet sein kann (UCPWO-398). */}
                        {fromSnapshot && (
                            <Alert status="warning" mb={4} data-test-id="px-connect-snapshot">
                                <AlertIcon />
                                Der Fragenkatalog konnte nicht von PX Connect geladen werden.
                                Angezeigt wird der zuletzt gespeicherte Stand
                                {snapshotSavedAtLabel ? ` vom ${snapshotSavedAtLabel}` : ''}.
                            </Alert>
                        )}
                        <PxQuestionList
                            questions={questions}
                            questionKeys={rootKeys}
                            dispatch={formDispatch}
                            errorKeys={errorKeys}
                            isDisabled={busy}
                        />
                    </>
                )}
            </ModalBody>
            <ModalFooter borderTopWidth={1} borderColor="gray.200">
                <HStack w="full" justify="space-between" align="flex-end">
                    <VStack align="flex-start" spacing={1}>
                        <Checkbox
                            data-test-id="px-connect-attach-fragenkatalog"
                            isChecked={attachFragenkatalog}
                            isDisabled={busy}
                            onChange={(event) => setAttachFragenkatalog(event.target.checked)}
                        >
                            Dieses Formular als PDF anhängen
                        </Checkbox>
                        {canAttachBericht && (
                            <Checkbox
                                data-test-id="px-connect-attach-bericht"
                                isChecked={attachBericht}
                                isDisabled={busy}
                                onChange={(event) => setAttachBericht(event.target.checked)}
                            >
                                Leckortungsbericht als PDF anhängen
                            </Checkbox>
                        )}
                    </VStack>
                    <HStack>
                        <Button
                            data-test-id="px-connect-cancel"
                            variant="outline"
                            isDisabled={busy}
                            onClick={onCancel}
                        >
                            Abbrechen
                        </Button>
                        <Button
                            data-test-id="px-connect-submit"
                            colorScheme="blue"
                            isLoading={submitting}
                            isDisabled={
                                !ready ||
                                loading ||
                                Boolean(error) ||
                                questions.length === 0 ||
                                savingDraft
                            }
                            onClick={onPrepareCorrespondence}
                        >
                            Korrespondenz vorbereiten
                        </Button>
                    </HStack>
                </HStack>
            </ModalFooter>
        </ModalContent>
    );
};
